import { getStyleToNumber } from '@lighthouse/tools'
import type { MutableRefObject } from 'react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

export interface DisplayInfo {
    visibleCount: number
    extraCount: number
}

export interface UseDynamicTagDisplayReturn {
    containerRef: MutableRefObject<HTMLDivElement | null>
    setTagRef: (index: number) => (el: HTMLDivElement | null) => void
    extraRef: MutableRefObject<HTMLDivElement | null>
    displayInfo: DisplayInfo
}

/**
 * useDynamicTagDisplay
 * @param tags 所有 tag 的数据数组（例如 string[]）
 * @returns { containerRef, setTagRef, extraRef, displayInfo }
 *   containerRef: 容器 DOM 的 ref
 *   setTagRef: 用于给每个 tag 设置 ref 的函数，传入 index 后返回 ref 回调
 *   extraRef: 用于测量额外标签（显示 "+N"）宽度的 ref
 *   displayInfo: { visibleCount, extraCount } 表示实际可显示 tag 数量和超出数量
 */
export function useDynamicTagDisplay(tags: string[]): UseDynamicTagDisplayReturn {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const tagRefs = useRef<(HTMLDivElement | null)[]>([])
    const extraRef = useRef<HTMLDivElement | null>(null)
    const [displayInfo, setDisplayInfo] = useState<DisplayInfo>({
        visibleCount: tags.length,
        extraCount: 0
    })

    const updateDisplayInfo = useCallback(() => {
        if (!containerRef.current) {
            return
        }
        const containerWidth = containerRef.current.clientWidth
        // const {gap} = containerRef.current.style
        const gap = getStyleToNumber(containerRef.current, 'gap')
        // 获取每个 tag 的宽度
        const widths = tagRefs.current.map(el => (el ? el.offsetWidth : 0))

        // 测量额外标签的宽度（例如预期显示 "+{tags.length}" 的内容）
        const extraWidth = extraRef.current ? extraRef.current.offsetWidth : 35

        let sum = 0
        let visibleCount = 0
        const total = widths.length

        // 遍历所有 tag 宽度
        for (let i = 0; i < total; i++) {
            if (i < total - 1) {
                // 若不是最后一个 tag，需要预留额外标签的宽度
                if (sum + widths[i] + gap + extraWidth <= containerWidth) {
                    sum += widths[i] + gap
                    visibleCount = i + 1
                } else {
                    break
                }
            } else {
                // 最后一个 tag直接判断是否可以完整显示
                if (sum + widths[i] <= containerWidth) {
                    visibleCount = i + 1
                }
            }
        }
        const extraCount = tags.length - visibleCount
        setDisplayInfo({ visibleCount, extraCount })
    }, [tags])

    useLayoutEffect(() => {
        // 布局更新的时候重新计算
        setTimeout(() => {
            updateDisplayInfo()
        }, 0)
        // 视窗变化的时候重新计算
        window.addEventListener('resize', updateDisplayInfo)
        return () => {
            window.removeEventListener('resize', updateDisplayInfo)
        }
    }, [updateDisplayInfo])

    // 返回一个 ref 设置函数，用于为每个 tag 元素设置 ref
    const setTagRef = useCallback(
        (index: number) => (el: HTMLDivElement | null) => {
            tagRefs.current[index] = el
        },
        []
    )

    return { containerRef, setTagRef, extraRef, displayInfo }
}
